import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { FaHome } from "react-icons/fa";
import { GET_OPEN_HOUSE_HOST_COUNTS } from "../../graphql/listingQueries";
import { UPCOMING_WINDOW_DAYS, TABS } from "../../constants/upcomingListingsConstants";

/**
 * The dashboard's entry point into open houses.
 *
 * Deliberately a button and not a list: the dashboard is a stats page, and the
 * only thing a broker needs from it at a glance is whether anything upcoming
 * is unstaffed. The list itself lives one click away on the Open Houses tab.
 *
 * Reads counts only, so the dashboard never pays for the rows.
 */
const OpenHousesQuickButton = ({ companyId }) => {
  const navigate = useNavigate();

  const { data, error } = useQuery(GET_OPEN_HOUSE_HOST_COUNTS, {
    variables: { companyId, withinDays: UPCOMING_WINDOW_DAYS },
    fetchPolicy: "cache-and-network",
  });

  const counts = data?.getCompanyUpcomingOpenHouses;

  // An office with nothing scheduled gets no button rather than an empty one.
  // A failed count is also silent — this is a shortcut, not a stat, and an
  // error banner here would be noise on a page that loaded fine.
  if (error || !counts?.totalCount) return null;

  const needsHost = counts.needsHostCount ?? 0;

  return (
    <button
      type="button"
      onClick={() =>
        navigate(
          `/company/${companyId}/upcoming-listings?tab=${TABS.OPEN_HOUSES}`
        )
      }
      className="inline-flex items-center gap-2 whitespace-nowrap rounded-sm border border-primary bg-white px-3 py-[5px] text-xs font-bold text-primary_dark hover:bg-[#f4f7e4]"
    >
      <FaHome size={13} />
      Open Houses
      <span
        className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
          needsHost
            ? "bg-[#fdeceb] text-[#cd2026]"
            : "bg-[#f4f7e4] text-[#5f6d0b]"
        }`}
      >
        {needsHost ? `${needsHost} need host` : "All covered"}
      </span>
    </button>
  );
};

export default OpenHousesQuickButton;
